import React, { memo, ReactElement } from 'react';
import { Chart } from '@dexma/ui-components';
import Branding from 'domain/entity/branding';
import PieData from 'domain/entity/widgets/PieData';
import { getColor, getPeriodFrom, getPeriodTo } from 'adapter/in/common/utils/widgetDataUtils';
import DataAvailability from 'adapter/in/common/components/widgets/DataAvailability';
import { PeriodType } from 'domain/entity/widgetData';

type PieProps = {
    height: number;
    width: number;
    isOnEdit: boolean;
    data: PieData;
    decimalSeparator: string;
    thousandSeparator: string;
    branding: Branding;
    isLoading: boolean;
    showLegend: boolean;
    showDataAvailability: boolean;
    dateFormat: string;
};

const fakeDataPie = [
    { name: 'something', y: 23 },
    { name: 'another', y: 26 },
];

const Pie = (props: PieProps): ReactElement => {
    const {
        height,
        width,
        isOnEdit,
        data,
        decimalSeparator,
        thousandSeparator,
        branding,
        isLoading,
        showLegend,
        showDataAvailability,
        dateFormat,
    } = props;

    const from = isLoading ? 'last seven days' : getPeriodFrom(data.description.period, dateFormat);
    let to = isLoading ? ' ' : getPeriodTo(data.description.period, dateFormat);

    to = to !== '' ? `- ${to}` : to;
    const subtitle = data.description.period.type === PeriodType.INTERVAL ? `${from} ${to}` : from.charAt(0).toUpperCase() + from.slice(1);

    const chartContract = {
        chart: {
            zoomType: isOnEdit || isLoading ? 'none' : 'x',
            height: height - 4,
            width: width - 4,
            type: 'pie',
            borderRadius: 8,
        },
        title: {
            text: isLoading ? 'Sample title' : data.description.title,
        },
        subtitle: {
            text: isLoading ? 'Sample subtitle' : subtitle,
        },
        tooltip: {
            enabled: !isLoading && !isOnEdit,
            shared: !isOnEdit,
            valueSuffix: ` ${data.units}`,
            valueDecimals: 2,
            pointFormat: '<span style="color:{point.color}">\u25CF</span> {point.name}: <b> {point.y} </b><br/>',
            headerFormat: isLoading ? undefined : `<span style="font-size:10px"> ${from} ${to}<span><br/>`,
        },
        plotOptions: {
            pie: {
                allowPointSelect: !isOnEdit,
                cursor: 'pointer',
                innerSize: '40%',
                dataLabels: {
                    enabled: !isOnEdit,
                    format: `{point.percentage:.1f}%`,
                },
                showInLegend: !isOnEdit,
            },
        },
        series: [
            {
                name: isLoading ? 'Sample title' : data.title,
                colorByPoint: true,

                data: isLoading
                    ? fakeDataPie
                    : data.series.map((series) => ({
                          name: series.title,
                          y: series.value,
                          color:
                              series.fillPattern === 'DASHED'
                                  ? {
                                        pattern: {
                                            path: {
                                                d: 'M 0 0 L 10 10 M 9 -1 L 11 1 M -1 9 L 1 11',
                                                strokeWidth: 3,
                                                fill: getColor(branding)(series.color),
                                            },
                                            width: 10,
                                            height: 10,
                                            color: getColor(branding)(series.color),
                                            opacity: 1,
                                        },
                                    }
                                  : getColor(branding)(series.color),
                      })),
            },
        ],
        legend: {
            enabled: showLegend,
        },
    };

    return (
        <>
            {!isLoading && !isOnEdit && showDataAvailability && <DataAvailability dataAvailability={data.dataAvailability} />}
            <Chart data-testid='pie-chart' thousandsSep={thousandSeparator} decimalPoint={decimalSeparator} options={chartContract} />
        </>
    );
};

export default memo(Pie);
